import * as React from 'react';
import { useEffect } from 'react';
import ProjectSummary from './ProjectSummary';
import { Route, Switch } from 'react-router-dom';
import { RouteTemplates } from '../navigation/routes';
import DevResourceModal from './resources/DevResourceModal';
import SetGoals from './setGoals/SetGoals';
import SelfSurvey from './survey/selfSurvey/SelfSurvey';
import CreateSurvey from './createSurvey/CreateSurvey';
import InviteRaters from './survey/raters/InviteRaters';
import FeedbackReport from './viewReport/FeedbackReport';
import ReportTutorial from './resources/ReportTutorial';
import { useDispatch } from 'react-redux';
import { useLoading, useError } from '../core/api/hooks';
import { ActionType } from '../core/redux/actionType';
import { PAGE_BG, PAGE_HEIGHT } from '../layout/pageStyle';
import { FLEX_COLUMN, FLEX_CENTERED } from '../layout/flexBox';
import { Spinner } from '../loading/Spinner';
import { COLORS } from '../globalStyle/colors';
import { css } from '@emotion/core';
import ErrorMsg from '../error/ErrorMsg';
import { Str } from '../localization/translationKeys';
import { Dispatch } from 'redux';
import { loadProjectSummaryAction } from './loadProjectSummaryAction';
import { loadProjectResourcesAction } from './resources/loadProjectResourcesAction';
import { loadParticipantRequirementsAction } from './requirements/loadParticipantRequirementsAction';
import { loadProjectIdAction } from './loadProjectIdAction';
import { useAppState } from '../core/redux/hooks';
import { projectLanguagesAction } from './messages/projectLanguagesAction';
import Account from '../account/Account';
import CustomerService from '../customerService/CustomerService';
import ChangeLanguageModal from '../localization/ChangeLanguageModal';
import { loadProjectsAction } from './loadProjectsAction';
import ProjectModal from './ProjectModal';
import ConfidentialityAgreementModal from '../legal/ConfidentialityAgreementModal';
import ChangePasswordModal from '../account/ChangePasswordModal';
import ChangeSecurityQuestionModal from '../account/ChangeSecurityQuestionModal';
import DevPlanRouter from './devPlan/devPlanAction/DevPlanRouter';
import RaterDirectorySearch from './survey/raters/directorySearch/RaterDirectorySearch';
import { raterLevelsAction } from './survey/raters/raterLevelsAction';
import Resource from './resources/CustomResource';
import { loadDevResourcesAction } from './resources/loadDevResourcesAction';
import ProjectStatusPoller from './ProjectStatusPoller';
import { useProjIds } from '../navigation/hooks';
import { PortalType } from '../navigation/portalType';
import { useMount } from '../core/hooks/useMount';
import { setPortalTypeAction } from '../core/redux/setPortalTypeAction';
import RemindRatersModal from './survey/raters/remindRaters/RemindRatersModal';
import { setDevPlanViewedAction } from './devPlan/api/setDevPlanViewedAction';
import { loadReportsAction } from './viewReport/api/loadReportsAction';
import { useCurrentUser } from '../auth/hooks';
import { logOutAction } from '../auth/login/api/logoutAction';
import { useWindowSize } from '../core/hooks/useWindowSize';
import EBook from './resources/EBook';


const styles = {
  spinner: css`
    width: 100px;
    height: 100px;
  `,
};


type Props = {};

const loadProjectInfo = (
  dispatch: Dispatch,
) => (
  projectId: number | undefined,
  participantId: number | undefined,
  languageId: number | undefined,
  hasProjects: boolean,
  userName: string | undefined,
): void => {
  if (
    projectId === undefined
    || participantId === undefined
    || languageId === undefined
    || projectId === -1
    || participantId === -1
    || languageId === -1
  ) {
    return;
  }

  if (!hasProjects) {
    dispatch(loadProjectsAction(participantId));
  }

  dispatch(loadProjectSummaryAction(projectId, participantId));
  dispatch(loadProjectResourcesAction(projectId));
  dispatch(loadParticipantRequirementsAction(projectId, participantId, languageId));
  dispatch(projectLanguagesAction(projectId));
  dispatch(raterLevelsAction(projectId));
  dispatch(loadDevResourcesAction(projectId));
  dispatch(setDevPlanViewedAction(false));
  dispatch(loadReportsAction(projectId, participantId, userName!));
};


/**
 * ProjectRouter: a component that routes to different project pages
 *
 * PureComponent: true
 * Redux Connected: false
 */
const ProjectRouter: React.FC<Props> = props => {
  const { isMobile } = useWindowSize();
  const dispatch     = useDispatch();
  const projIds      = useProjIds();
  const currentUser  = useCurrentUser();

  useMount(() => { dispatch(setPortalTypeAction(PortalType.PROJECT)); });

  if (!currentUser?.hasPortalLoginPermission) {
    dispatch(logOutAction(isMobile));
  }

  let isLoading = useLoading(ActionType.LoadProjectSummaryRequest);
  isLoading     = useLoading(ActionType.LoadParticipantRequirementsRequest) || isLoading;
  isLoading     = useLoading(ActionType.LoadProjectResourcesRequest) || isLoading;
  isLoading     = useLoading(ActionType.LoadProjectIdRequest, projIds.projDisplayId) || isLoading;
  isLoading     = useLoading(ActionType.LoadProjectsRequest) || isLoading;
  isLoading     = useLoading(ActionType.LoadDevResourcesRequest) || isLoading;

  let hasError = useError(ActionType.LoadProjectSummaryRequest);
  hasError     = useError(ActionType.LoadParticipantRequirementsRequest) || hasError;
  hasError     = useError(ActionType.LoadProjectResourcesRequest) || hasError;
  hasError     = useError(ActionType.LoadProjectIdRequest, projIds.projDisplayId) || hasError;
  hasError     = useError(ActionType.LoadProjectsRequest) || hasError;
  hasError     = useError(ActionType.LoadDevResourcesRequest) || hasError;

  const hasProjects = useAppState(state => !!state.projects?.length);

  const languageId = useAppState(state => state.locale.lang.languageId);
  const project    = useAppState(state => state.projectSummary);
  const userName   = useAppState(state => state.auth.currentUser?.userName);

  const loadProjects = React.useCallback(
    () => loadProjectInfo(dispatch)(projIds.projId, projIds.participantId, languageId, hasProjects, userName),
    [projIds.projId, projIds.participantId, hasProjects, languageId, userName, dispatch],
  );

  useEffect(() => {dispatch(loadProjectIdAction(projIds.projDisplayId));}, [projIds.projDisplayId, dispatch]);
  useEffect(loadProjects, [loadProjects]);

  const completeResponses = project?.completeResponses || 0;
  useEffect(
    () => {
      dispatch(loadParticipantRequirementsAction(projIds.projId, projIds.participantId, languageId));
    },
    [projIds.projId, projIds.participantId, languageId, completeResponses, dispatch],
  );

  if (isLoading || hasError) {
    return (
      <div css={[PAGE_BG, PAGE_HEIGHT, FLEX_COLUMN, FLEX_CENTERED]}>
        {
          isLoading
            ? <Spinner fill={COLORS.GREEN} css={styles.spinner} />
            : <ErrorMsg error={Str.LoadFailed} showRetry={true} onRetry={loadProjects} />
        }
      </div>
    );
  }

  if (project?.confidentialRequired) {
    return <ConfidentialityAgreementModal />;
  }

  return (
    <>
      <Switch key={projIds.projDisplayId}>
        <Route path={RouteTemplates.SetGoals} component={SetGoals} />
        <Route path={RouteTemplates.SelfSurvey} component={SelfSurvey} />

        {/* Dev Plan */}
        <Route path={RouteTemplates.DevPlan} component={DevPlanRouter} />

        <Route path={RouteTemplates.CreateSurvey} component={CreateSurvey} />
        <Route path={RouteTemplates.InviteRaters} component={InviteRaters} />
        <Route path={RouteTemplates.SearchDirectory} component={RaterDirectorySearch} />
        <Route path={RouteTemplates.FeedbackReport} component={FeedbackReport} />

        <Route path={RouteTemplates.ReportTutorial} component={ReportTutorial} />
        <Route path={RouteTemplates.EBook} component={EBook} />
        <Route path={RouteTemplates.CustomResource} component={Resource} />
        <Route path={RouteTemplates.Account} component={Account} />
        <Route path={RouteTemplates.CustomerService} component={CustomerService} />

        <Route path={RouteTemplates.Project} component={ProjectSummary} />
      </Switch>

      {/* Modals */}
      <Switch>
        <Route path={RouteTemplates.SelectProject} component={ProjectModal} />
        <Route path={RouteTemplates.Language} component={ChangeLanguageModal} />

        {/*Dev Resources*/}
        <Route path={RouteTemplates.DevResources} component={DevResourceModal} />
        <Route path={RouteTemplates.DevResources_DevPlan} component={DevResourceModal} />

        <Route path={RouteTemplates.ChangePassword} component={ChangePasswordModal} />
        <Route path={RouteTemplates.ChangeSecurityQuestion} component={ChangeSecurityQuestionModal} />

        {/* Raters */}
        <Route path={RouteTemplates.RemindRatersProjDash} component={RemindRatersModal} />
        <Route path={RouteTemplates.RemindRaters} component={RemindRatersModal} />
      </Switch>

      {/* Non-Rendering */}
      <ProjectStatusPoller key={`ProjectStatusPoller-${projIds.projDisplayId}`} />
    </>
  );
};

export default ProjectRouter;
